import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { 
  FaChevronLeft, 
  FaTools, 
  FaEnvelope, 
  FaShieldAlt,
  FaCheckCircle,
  FaMicrochip
} from 'react-icons/fa';
import { productsData } from '../../data/productsData';
import { API_CONFIG } from '../../config/api';
import './Products.css';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // 1. Check local fallback first
    const cleanId = String(id).toLowerCase().replace(/[^a-z0-9]/g, '');
    const matchedLocal = productsData.find(
      (p) => String(p.id) === String(id) || 
             p.slug === String(id) || 
             p.name.toLowerCase() === String(id).toLowerCase() ||
             (p.slug && p.slug.toLowerCase().replace(/[^a-z0-9]/g, '') === cleanId) ||
             (p.name && p.name.toLowerCase().replace(/[^a-z0-9]/g, '') === cleanId)
    );

    if (matchedLocal) {
      setProduct(matchedLocal);
    }

    // 2. Fetch directly from backend API
    fetch(`${API_CONFIG.BASE_URL}/products.php?id=${id}`)
      .then(res => {
        if (!res.ok) throw new Error('API fetch error');
        return res.json();
      })
      .then(apiData => {
        if (apiData && apiData.name) {
          const formatted = {
            id: apiData.id,
            name: apiData.name,
            description: apiData.description || '',
            specs: apiData.specs || '',
            sequence: apiData.sequence || 0,
            images: apiData.images && apiData.images.length > 0
              ? apiData.images.map(img => ({
                  ...img,
                  image_path: API_CONFIG.getImageUrl(img.image_path)
                }))
              : (matchedLocal ? matchedLocal.images : [{ id: 0, image_path: '/Photo/peace-i17-pro-poster.jpg', label: 'Main' }])
          };
          setProduct(formatted);
        }
        setLoading(false);
      })
      .catch(() => {
        if (!matchedLocal && productsData.length > 0) {
          setProduct(productsData[0]);
        }
        setLoading(false);
      });
  }, [id]);

  if (loading && !product) {
    return (
      <div className="product-detail-page">
        <Container className="py-5 text-center">
          <div className="catalog-spinner" />
          <p className="mt-3 text-secondary">Loading device details...</p>
        </Container>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <Container className="py-5 text-center">
          <h2 className="text-white">Product Not Found</h2>
          <p className="text-secondary mt-2">The requested mobile model could not be found.</p>
          <Link to="/products" className="apple-btn apple-btn-primary mt-3">
            Back to All Products
          </Link>
        </Container>
      </div>
    );
  }

  const images = product.images && product.images.length > 0 
    ? product.images 
    : [{ id: 0, image_path: '/Photo/peace-i17-pro-poster.jpg', label: 'Main' }];

  // Formatter for description text to support rich multiline paragraphs / bullet lines
  const renderBeautifiedDescription = (text) => {
    if (!text) return <p className="text-secondary">No description available.</p>;

    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

    return (
      <div className="beautified-description-body">
        {lines.map((line, idx) => {
          if (line.startsWith('-') || line.startsWith('•') || line.startsWith('*')) {
            const cleanText = line.replace(/^[-•*]\s*/, '');
            return (
              <div key={idx} className="desc-bullet-row">
                <FaCheckCircle className="desc-bullet-icon" />
                <span>{cleanText}</span>
              </div>
            );
          }

          const colonIdx = line.indexOf(':');
          if (colonIdx > 0 && colonIdx < 30) {
            const label = line.substring(0, colonIdx).trim();
            const value = line.substring(colonIdx + 1).trim();
            return (
              <div key={idx} className="desc-keyval-row">
                <span className="desc-key">{label}</span>
                <span className="desc-val">{value}</span>
              </div>
            );
          }

          return (
            <p key={idx} className="desc-paragraph">
              {line}
            </p>
          );
        })}
      </div>
    );
  };

  // Safe parser for technical specifications from backend
  const renderFormattedSpecs = (specsData) => {
    if (!specsData) return null;

    let items = [];

    if (typeof specsData === 'string') {
      items = specsData
        .split(/[\n|]+/)
        .map(s => s.trim())
        .filter(Boolean);
    } else if (typeof specsData === 'object' && !Array.isArray(specsData)) {
      items = Object.entries(specsData).map(([key, val]) => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${val}`);
    } else if (Array.isArray(specsData)) {
      items = specsData.map(s => String(s).trim()).filter(Boolean);
    }

    if (items.length === 0) return null;

    return (
      <div className="backend-specs-grid">
        {items.map((item, idx) => {
          const colonIndex = item.indexOf(':');
          if (colonIndex > 0) {
            const key = item.substring(0, colonIndex).trim();
            const val = item.substring(colonIndex + 1).trim();
            return (
              <div key={idx} className="backend-spec-card">
                <span className="spec-card-key">{key}</span>
                <span className="spec-card-val">{val}</span>
              </div>
            );
          }
          return (
            <div key={idx} className="backend-spec-card single-line">
              <FaCheckCircle className="spec-bullet-icon" />
              <span className="spec-card-val">{item}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="product-detail-page">
      {/* Sub-navigation breadcrumb */}
      <div className="detail-breadcrumb-bar">
        <Container>
          <div className="breadcrumb-content">
            <Link to="/products" className="back-link">
              <FaChevronLeft className="back-arrow" /> Back to Products
            </Link>
          </div>
        </Container>
      </div>

      <Container className="py-5">
        <div className="detail-showcase-container">
          <Row className="gy-5 align-items-center">
            {/* Left Column: Interactive Image Gallery */}
            <Col lg={6} md={12}>
              <div className="detail-gallery-wrapper">
                {/* Main Featured Image Stage */}
                <div className="detail-main-stage">
                  <img 
                    src={images[currentImageIndex] ? images[currentImageIndex].image_path : images[0].image_path} 
                    alt={product.name}
                    className="detail-featured-image"
                  />
                </div>

                {/* Thumbnails Navigation Row */}
                {images.length > 1 && (
                  <div className="detail-thumbnails-row">
                    {images.map((img, idx) => (
                      <button
                        key={img.id || idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`detail-thumb-btn ${idx === currentImageIndex ? 'active' : ''}`}
                        aria-label={`View photo ${idx + 1}`}
                      >
                        <img 
                          src={img.image_path} 
                          alt={`${product.name} thumbnail ${idx + 1}`}
                          className="thumb-img"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Col>

            {/* Right Column: Title, Beautified Backend Description & Support Actions */}
            <Col lg={6} md={12}>
              <div className="detail-info-card">
                <div className="detail-header-block">
                  <span className="detail-badge-pill">Peace Smartphone</span>
                  <h1 className="detail-title">{product.name}</h1>
                </div>

                <hr className="detail-divider" />

                {/* Beautified Backend Description */}
                <div className="detail-section-block">
                  {renderBeautifiedDescription(product.description)}
                </div>

                {/* Action Links */}
                <div className="detail-cta-group mt-4">
                  <Link to="/service-centre" className="apple-btn apple-btn-primary detail-buy-btn">
                    <FaTools className="me-2" /> Find Service Center
                  </Link>
                  <Link to="/contact" className="apple-btn apple-btn-secondary detail-secondary-btn">
                    <FaEnvelope className="me-2" /> Contact & Inquiries
                  </Link>
                </div>

                {/* Assurance & Warranty Footnote */}
                <div className="detail-perks-box mt-3">
                  <div className="detail-perk">
                    <FaShieldAlt className="perk-icon" />
                    <span>Official Manufacturer Warranty & Nationwide Customer Support</span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Technical Specifications Section */}
        {product.specs && (
          <Row className="mt-5">
            <Col xs={12}>
              <div className="detail-specs-table-card">
                <div className="specs-table-header">
                  <h3>
                    <FaMicrochip className="me-2 text-primary" /> Technical Specifications
                  </h3>
                  <span className="specs-table-model">{product.name}</span>
                </div>

                <div className="p-2">
                  {renderFormattedSpecs(product.specs)}
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default ProductDetail;
